import { Link } from "wouter";
import { ArrowDown, ArrowRight, Clock3, Compass, Plug, UserRoundPlus } from "lucide-react";
import { StartFreeButton } from "@/components/marketing/cta";
import { PROOF_LINE, PROOF_TILES } from "@/data/proof-tiles";
import { TRIAL_DAYS } from "@/lib/marketing/pricing-copy";

const screenshots = [
  {
    src: "/images/home-v2/messages.webp",
    alt: "Actual PadPulse member messages view, with illustrative demo data",
    title: "Messages, together.",
    caption: "A shared place to review member conversations across houses.",
  },
  {
    src: "/images/home-v2/maintenance.webp",
    alt: "Actual PadPulse maintenance view, with illustrative demo data",
    title: "Work you can verify.",
    caption: "Track a request, coordinate the vendor, and review submitted proof.",
  },
  {
    src: "/images/home-v2/numbers.webp",
    alt: "Actual PadPulse property financial view, with illustrative demo data",
    title: "House-by-house numbers.",
    caption: "See your property finances without stitching together exports.",
  },
];

export function HomeV2Hero() {
  return (
    <section
      className="relative isolate overflow-hidden bg-brand-dark px-4 pb-14 pt-28 text-white md:pb-20 md:pt-36"
      data-testid="section-hero"
    >
      <div
        className="pointer-events-none absolute inset-0 -z-10 opacity-40"
        aria-hidden="true"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 76% 22%, rgba(226,157,103,.25), transparent 38%), linear-gradient(135deg, transparent 48%, rgba(255,255,255,.035) 48.2%, transparent 48.5%)",
        }}
      />
      <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[.9fr_1.1fr] lg:gap-14">
        <div className="max-w-xl">
          <p className="text-sm font-semibold uppercase tracking-[.16em] text-brand-accent" data-testid="text-eyebrow">
            The day-to-day home base for PadSplit hosts
          </p>
          <h1
            className="mt-5 text-[2.65rem] font-bold leading-[1.04] tracking-[-.04em] text-[#fff9ef] sm:text-5xl lg:text-[3.65rem]"
            data-testid="text-page-heading"
          >
            Run the houses.
            <br />
            Not the scramble.
          </h1>
          <p className="mt-6 max-w-lg text-lg leading-8 text-[#dbe5e4]" data-testid="text-page-sub">
            PadPulse brings PadSplit member messages, booking requests, maintenance, smart locks and property
            finances into one practical workspace—built by a host who knows the work.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <StartFreeButton
              ctaId="home_v2_hero-start-free"
              location="home_v2_hero"
              className="min-h-12 justify-center px-7"
            />
          </div>
          <p className="mt-4 text-sm text-[#b9cacb]">
            {TRIAL_DAYS} days free · No card to start · About 3 minutes to connect PadSplit
          </p>
          <p className="mt-2 text-xs leading-5 text-[#9fb5b7]">
            PadPulse is independent software; it connects to your PadSplit host account.
          </p>
        </div>

        <figure className="relative mx-auto w-full max-w-[600px] lg:mr-0">
          <div className="absolute -inset-3 -rotate-2 rounded-[2rem] border border-white/10 bg-white/[.04]" aria-hidden="true" />
          <img
            src="/images/home-v2/coliving.webp"
            width={1440}
            height={1080}
            loading="eager"
            decoding="async"
            alt="Illustrated cutaway of a shared co-living house, showing private rooms and shared spaces"
            className="relative aspect-[4/3] w-full rounded-[1.5rem] border border-white/15 object-cover shadow-2xl shadow-black/25"
          />
          <figcaption className="relative mt-3 flex items-start gap-2 px-1 text-xs leading-5 text-[#bdcdcc]">
            <span className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full bg-brand-accent" aria-hidden="true" />
            A co-living house, illustrated—not a product screenshot.
          </figcaption>
        </figure>
      </div>
      <a
        href="#home-v2-product"
        className="mx-auto mt-12 flex w-fit items-center gap-2 text-sm font-medium text-[#cfdbd8] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-accent"
      >
        See how hosts use it <ArrowDown size={15} aria-hidden="true" />
      </a>
    </section>
  );
}

export function HomeV2Proof({ includeSavings = false }: { includeSavings?: boolean } = {}) {
  return (
    <section className="bg-[#f6f3eb] px-4 py-14 md:py-20" data-testid="section-proof">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-9 md:grid-cols-[.8fr_1.2fr] md:items-end">
          <div>
            <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">Built for the daily details</p>
            <h2 className="mt-3 max-w-md text-3xl font-bold leading-tight tracking-tight text-[#183347] md:text-4xl">
              One place for the work that keeps a house running.
            </h2>
          </div>
          <p className="max-w-2xl text-base leading-7 text-[#52636a] md:justify-self-end">
            {PROOF_LINE} Bring the conversations, people, upkeep and finances together—without pretending the house
            runs itself.
          </p>
        </div>
        <div className={`mt-9 grid gap-3 ${includeSavings ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
          {(includeSavings ? [...PROOF_TILES, { value: "$250+", label: "Saved per direct booking vs. PadSplit fees" }] : PROOF_TILES).map((item, index) => (
            <div
              key={item.value}
              className="flex min-h-24 items-center gap-4 rounded-xl border border-[#d8d8cf] bg-[#fffdf8] px-5 py-4"
            >
              <span className="font-mono text-2xl font-bold tracking-tight text-brand">{item.value}</span>
              <span className="text-sm font-medium leading-5 text-[#43555c]">
                {index === 1 ? "AI-drafted replies · beta access" : item.label}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-4 max-w-4xl text-xs leading-5 text-[#657276]">
          AI reply drafting is available by beta access only. Continuous Auto Draft requires beta access and must be
          enabled; signing up or choosing a paid plan alone does not grant access.
        </p>
        <p className="mt-2 max-w-4xl text-xs leading-5 text-[#52636a]">
          The 60s tile refers to the send/reconciliation cycle, not guaranteed inbound arrival. Incoming messages
          depend on PadSplit harvesting and can take longer.
        </p>
      </div>
    </section>
  );
}

export function HomeV2ProductEvidence() {
  return (
    <section id="home-v2-product" className="bg-[#fffdf8] px-4 py-16 md:py-24 scroll-mt-20" aria-labelledby="home-v2-evidence-title">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">A look inside</p>
            <h2 id="home-v2-evidence-title" className="mt-3 text-3xl font-bold tracking-tight text-[#183347] md:text-4xl">
              Real screens. Useful context.
            </h2>
            <p className="mt-3 max-w-xl leading-7 text-[#52636a]">
              Take a look at the actual product before you sign up. These real product screens use illustrative demo data, not live operating results.
            </p>
          </div>
          <p className="max-w-sm text-sm leading-6 text-[#627176]">
            PadPulse helps you organize the work. Hosts stay in control of maintenance, devices and their team.
          </p>
        </div>
        <div className="mt-10 space-y-12">
          {screenshots.map((screen, index) => (
            <figure
              key={screen.src}
              className={`grid min-w-0 items-center gap-5 md:grid-cols-[minmax(0,1.2fr)_minmax(220px,.8fr)] md:gap-10 ${
                index % 2 === 1 ? "md:[&>img]:order-2" : ""
              }`}
            >
              <img
                src={screen.src}
                width={1440}
                height={1000}
                loading="lazy"
                decoding="async"
                alt={screen.alt}
                className="block aspect-[36/25] w-full min-w-0 rounded-xl border border-[#dddcd4] bg-[#f4f1e8] object-cover shadow-[0_16px_40px_-28px_rgba(16,43,64,.45)]"
              />
              <figcaption className="max-w-sm">
                <span className="text-xs font-bold uppercase tracking-[.14em] text-brand">0{index + 1}</span>
                <h3 className="mt-2 text-xl font-bold text-[#183347]">{screen.title}</h3>
                <p className="mt-2 text-sm leading-6 text-[#52636a]">{screen.caption}</p>
                <p className="mt-3 text-xs font-medium text-[#52636a]">Actual product screen · illustrative demo data</p>
                <a href={screen.src} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex min-h-10 items-center font-semibold text-brand underline underline-offset-4" aria-label={`Open full-size ${screen.title} product screenshot`}>
                  View full-size screen <ArrowRight size={16} className="ml-1" aria-hidden="true" />
                </a>
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="mt-12 grid gap-3 rounded-2xl border border-[#d8d8cf] bg-[#f6f3eb] p-5 sm:grid-cols-[auto_1fr] sm:items-center sm:p-7">
          <div className="flex flex-wrap gap-2 sm:flex-col sm:items-start">
            <span className="rounded bg-[#dce7dc] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#36574e]">
              Current
            </span>
            <span className="rounded bg-[#e9e4d8] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#715d42]">
              Read-only
            </span>
          </div>
          <p className="mt-2 text-sm leading-6">
            <span className="font-semibold text-[#36574e]">Connect supported U.S. banks through Stripe-hosted linking.</span>{" "}
            <span className="text-[#715d42]">USD accounts only; institution and account support varies. Review imported transactions before P&amp;L inclusion; test money stays excluded. Bank feeds are separate from subscription payment.</span>
          </p>
        </div>
      </div>
    </section>
  );
}

export function HomeV2Setup() {
  const steps = [
    {
      icon: UserRoundPlus,
      number: "01",
      title: "Create your account",
      body: "Start with your host account. No card is needed for the free trial.",
    },
    {
      icon: Plug,
      number: "02",
      title: "Connect PadSplit",
      body: "Connect your PadSplit host account. This connection takes about 3 minutes.",
    },
    {
      icon: Compass,
      number: "03",
      title: "Add the extras when ready",
      body: "Devices, vendors and read-only bank feeds are optional setup steps. Add them when they fit your operation.",
    },
  ];

  return (
    <section className="bg-[#e9eee9] px-4 py-16 md:py-20" aria-labelledby="home-v2-setup-title">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-9 md:grid-cols-[.8fr_1.2fr] md:items-end">
          <div>
            <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">A simple start</p>
            <h2 id="home-v2-setup-title" className="mt-3 max-w-md text-3xl font-bold leading-tight text-[#183347] md:text-4xl">
              Get connected first. Build from there.
            </h2>
          </div>
          <p className="max-w-xl text-base leading-7 text-[#52636a]">
            Connecting PadSplit is the quick part. You can add devices, vendors and banking details later—when it
            makes sense for your houses.
          </p>
        </div>
        <ol className="mt-9 grid gap-3 md:grid-cols-3">
          {steps.map(({ icon: Icon, number, title, body }) => (
            <li key={number} className="rounded-xl border border-[#d2dbd5] bg-[#fffdf8] p-5 sm:p-6">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs font-bold tracking-wider text-[#738078]">{number}</span>
                <Icon size={20} className="text-brand" aria-hidden="true" />
              </div>
              <h3 className="mt-5 text-lg font-bold text-[#183347]">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-[#52636a]">{body}</p>
              {number === "02" ? (
                <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#e9eee9] px-3 py-1.5 text-xs font-semibold text-[#36574e]">
                  <Clock3 size={14} aria-hidden="true" /> About 3 minutes for this step
                </p>
              ) : null}
            </li>
          ))}
        </ol>
        <p className="mt-5 text-sm leading-6 text-[#52636a]">
          About 3 minutes is for the connection step—not full onboarding or completed imports. Initial imports can take longer.
        </p>
        <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <StartFreeButton
            ctaId="home_v2_setup-start-free"
            location="home_v2_setup"
            label="Create your account"
            className="min-h-11 px-6"
          />
          <Link
            href="/research"
            className="inline-flex min-h-11 items-center gap-2 rounded-lg px-3 font-semibold text-brand hover:bg-white/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            data-testid="link-home-v2-research"
          >
            Explore free research tools <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function HomeV2Founder() {
  return (
    <section className="bg-[#fffdf8] px-4 py-16 md:py-20" aria-label="A note from the founder">
      <div className="mx-auto grid max-w-5xl items-center gap-8 rounded-2xl border border-[#dddcd4] bg-[#f6f3eb] p-6 sm:p-9 md:grid-cols-[180px_1fr] md:gap-12">
        <img
          src="/images/home-v2/founder.webp"
          width={360}
          height={360}
          loading="lazy"
          decoding="async"
          alt="Travis Schwartz, PadSplit host and founder of PadPulse"
          className="mx-auto h-36 w-36 rounded-full border-4 border-[#fffdf8] bg-[#e4e1d8] object-cover object-top md:h-40 md:w-40"
        />
        <figure>
          <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">Built from host experience</p>
          <blockquote className="mt-3 text-xl font-medium leading-8 tracking-[-.015em] text-[#183347] md:text-2xl">
            “If you click on a message on PadSplit, it marks it as read right away… now it falls through the cracks,
            especially at scale.”
          </blockquote>
          <figcaption className="mt-4 text-sm text-[#5d6b6b]">
            <span className="font-semibold text-[#183347]">Travis Schwartz</span> · PadSplit host and founder of
            PadPulse
          </figcaption>
        </figure>
      </div>
    </section>
  );
}
